import { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import api from "../../lib/api";
import Button from "../ui/Button";
import Input from "../ui/Input";
import { formatDay, todayISO } from "../../lib/utils";

// Different hours on one particular date (a holiday eve, a late opening).
// They replace the weekly hours for that date; add two for a day with a break.
export default function SpecialHours({ staffMember }) {
  const [entries, setEntries] = useState(null);
  const [form, setForm] = useState({ date: "", start_time: "09:00", end_time: "13:00" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = () =>
    api
      .get(`/owner/staff/${staffMember.id}/special-hours`)
      .then(({ data }) => setEntries(data || []))
      .catch(() => setEntries([]));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffMember.id]);

  const add = async () => {
    if (!form.date || !form.start_time || !form.end_time || form.start_time >= form.end_time) {
      setError("Pick a date, and a closing time later than the opening time.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.post(`/owner/staff/${staffMember.id}/special-hours`, form);
      setForm({ ...form, date: "" });
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || "Could not save these hours.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/owner/staff/${staffMember.id}/special-hours/${id}`).catch(() => {});
    load();
  };

  return (
    <div className="mt-2 space-y-2">
      <p className="text-xs text-muted-foreground">
        Different hours on one date. They replace the usual hours for that date — add two for a day with a break.
      </p>
      {entries === null ? (
        <p className="text-xs text-muted-foreground">Loading…</p>
      ) : entries.length === 0 ? (
        <p className="text-xs text-muted-foreground">No special hours set.</p>
      ) : (
        <ul className="space-y-1">
          {entries.map((entry) => (
            <li key={entry.id} className="flex items-center justify-between text-xs">
              <span>
                {formatDay(entry.date)} · {entry.start_time?.slice(0, 5)}–{entry.end_time?.slice(0, 5)}
              </span>
              <button onClick={() => remove(entry.id)} aria-label="Remove" className="text-muted-foreground hover:text-destructive">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <Input
          type="date"
          min={todayISO()}
          value={form.date}
          onChange={(e) => setForm({ ...form, date: e.target.value })}
          aria-label="Date"
          className="w-auto"
        />
        <input
          type="time"
          value={form.start_time}
          onChange={(e) => setForm({ ...form, start_time: e.target.value })}
          aria-label="Opens"
          className="h-8 rounded-md border border-input px-1"
        />
        <span>–</span>
        <input
          type="time"
          value={form.end_time}
          onChange={(e) => setForm({ ...form, end_time: e.target.value })}
          aria-label="Closes"
          className="h-8 rounded-md border border-input px-1"
        />
        <Button size="sm" variant="outline" onClick={add} disabled={saving}>
          {saving ? "Saving…" : "Add hours"}
        </Button>
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
